// Sphère des alphabets : grille de particules en mappage cube-sphère
// (6 faces, pas de pôles), déplacée par un curl noise multi-échelles
// (transposition du plugin After Effects "Trapcode Form", cf. passation
// dans le dossier de travail sillage). Chaque particule prend la couleur
// du territoire d'alphabet le plus proche, dont la taille grandit (par
// saturation exponentielle, jamais de limite dure) avec le nombre de
// punchlines associées à cet alphabet.
//
// Dépend de THREE en tant que variable GLOBALE (pas d'import ES module) --
// c'est la convention déjà en place pour sillageMain.js (three.min.js
// vendorisé chargé en <script> classique avant le bundle, cf.
// scripts/build.mjs). La page de réglage (sillage-sphere-tuning.html)
// charge donc aussi three.min.js en global avant ce module.
import { SCRIPT_ORDER, ALPHABET_LABELS, DEFAULT_SPHERE_SETTINGS } from './alphabetSphereDefaults.js';

export { SCRIPT_ORDER, ALPHABET_LABELS, DEFAULT_SPHERE_SETTINGS };

const GOLDEN_ANGLE = 137.508;

const ALPHABET_COLORS = SCRIPT_ORDER.map((_, i) =>
  new THREE.Color().setHSL(((i * GOLDEN_ANGLE) % 360) / 360, 0.85, 0.6)
);

// Couleurs Latin/Coréen inversées (demande Rudy) -- les ancrages
// (ALPHABET_ANCHORS, position sur la sphère) restent inchangés, seule la
// couleur attribuée à chaque écriture change de place.
{
  const latnIdx = SCRIPT_ORDER.indexOf('Latn');
  const hangIdx = SCRIPT_ORDER.indexOf('Hang');
  const swap = ALPHABET_COLORS[latnIdx];
  ALPHABET_COLORS[latnIdx] = ALPHABET_COLORS[hangIdx];
  ALPHABET_COLORS[hangIdx] = swap;
}

// Répartition des 10 ancrages sur la sphère (Fibonacci sphere = uniforme).
const ALPHABET_ANCHORS = SCRIPT_ORDER.map((_, i, arr) => {
  const n = arr.length;
  const y = 1 - (i / (n - 1)) * 2;
  const r = Math.sqrt(Math.max(0, 1 - y * y));
  const theta = Math.PI * (3 - Math.sqrt(5)) * i;
  return new THREE.Vector3(Math.cos(theta) * r, y, Math.sin(theta) * r);
});

// Grille (u,v) classique mappée en coordonnées sphériques fait converger
// toutes les colonnes en un point exact à chaque pôle (artefact
// "entonnoir"). Le mappage cube-sphère (6 grilles plates repliées, comme un
// dé) garde la structure de grille sans aucune convergence.
function buildCubeSphereGrid(colsPerFace, rowsPerFace) {
  const faceFns = [
    (a, b) => [1, b, -a],
    (a, b) => [-1, b, a],
    (a, b) => [a, 1, -b],
    (a, b) => [a, -1, b],
    (a, b) => [a, b, 1],
    (a, b) => [-a, b, -1],
  ];
  const count = faceFns.length * colsPerFace * rowsPerFace;
  const positions = new Float32Array(count * 3);
  let idx = 0;
  for (const faceFn of faceFns) {
    for (let j = 0; j < rowsPerFace; j++) {
      const b = (j / (rowsPerFace - 1)) * 2 - 1;
      for (let i = 0; i < colsPerFace; i++) {
        const a = (i / (colsPerFace - 1)) * 2 - 1;
        const [x, y, z] = faceFn(a, b);
        const len = Math.sqrt(x * x + y * y + z * z);
        positions[idx * 3] = x / len;
        positions[idx * 3 + 1] = y / len;
        positions[idx * 3 + 2] = z / len;
        idx++;
      }
    }
  }
  return positions;
}

// Couleur de territoire par sommet -- meme formule que l'ancienne boucle
// GLSL (deplacee ici pour ne plus tourner a chaque frame, cf. commentaire
// dans VERTEX_SHADER) : chaque sommet prend la couleur de l'ancrage
// d'alphabet le plus influent a sa position DE BASE (fixe), avec un melange
// vers le gris neutre selon la force de cette influence. Ne depend que de
// `positions` (fixe par sommet) et de weights/sigmaBase/weightScale --
// appelee uniquement a la construction de la geometrie et quand l'un de ces
// trois change (setWeights/applySettings), jamais par frame.
function computeTerritoryColors(positions, weightsArray, sigmaBase, weightScale) {
  const count = positions.length / 3;
  const colors = new Float32Array(count * 3);
  const neutral = 0.85;
  const safeWeightScale = Math.max(weightScale, 0.001);
  for (let v = 0; v < count; v++) {
    const dx = positions[v * 3], dy = positions[v * 3 + 1], dz = positions[v * 3 + 2];
    let bestInfluence = 0;
    let bestColor = null;
    for (let i = 0; i < ALPHABET_ANCHORS.length; i++) {
      const w = weightsArray[i];
      if (w <= 0) continue;
      const anchor = ALPHABET_ANCHORS[i];
      const dot = Math.min(1, Math.max(-1, dx * anchor.x + dy * anchor.y + dz * anchor.z));
      const angDist = Math.acos(dot);
      const satur = 1 - Math.exp(-w / safeWeightScale);
      const sigma = sigmaBase * (0.15 + satur * 1.6);
      const influence = Math.exp(-(angDist * angDist) / (2 * sigma * sigma));
      if (influence > bestInfluence) { bestInfluence = influence; bestColor = ALPHABET_COLORS[i]; }
    }
    const t = Math.min(1, Math.max(0, bestInfluence));
    const r = bestColor ? bestColor.r : 0, g = bestColor ? bestColor.g : 0, b = bestColor ? bestColor.b : 0;
    colors[v * 3] = neutral + (r - neutral) * t;
    colors[v * 3 + 1] = neutral + (g - neutral) * t;
    colors[v * 3 + 2] = neutral + (b - neutral) * t;
  }
  return colors;
}

const VERTEX_SHADER = `
  uniform vec3 uNoiseOffset;
  uniform float uRadius;
  uniform float uFScale;
  uniform int uComplexity;
  uniform float uOctMult;
  uniform float uOctScale;
  uniform float uDisplace;
  uniform float uContain;
  uniform float uFeather;
  uniform float uRimBoost;
  uniform float uSize;
  attribute vec3 aColor;

  varying float vN;
  varying vec3 vColor;

  // Simplex noise 3D (Ashima Arts / Ian McEwan, MIT).
  vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
  vec4 mod289(vec4 x){return x-floor(x*(1.0/289.0))*289.0;}
  vec4 permute(vec4 x){return mod289(((x*34.0)+1.0)*x);}
  vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
  float snoise(vec3 v){
    const vec2 C=vec2(1.0/6.0,1.0/3.0);
    const vec4 D=vec4(0.0,0.5,1.0,2.0);
    vec3 i=floor(v+dot(v,C.yyy));
    vec3 x0=v-i+dot(i,C.xxx);
    vec3 g=step(x0.yzx,x0.xyz);
    vec3 l=1.0-g;
    vec3 i1=min(g.xyz,l.zxy);
    vec3 i2=max(g.xyz,l.zxy);
    vec3 x1=x0-i1+C.xxx;
    vec3 x2=x0-i2+C.yyy;
    vec3 x3=x0-D.yyy;
    i=mod289(i);
    vec4 p=permute(permute(permute(
      i.z+vec4(0.0,i1.z,i2.z,1.0))
      +i.y+vec4(0.0,i1.y,i2.y,1.0))
      +i.x+vec4(0.0,i1.x,i2.x,1.0));
    float n_=0.142857142857;
    vec3 ns=n_*D.wyz-D.xzx;
    vec4 j=p-49.0*floor(p*ns.z*ns.z);
    vec4 x_=floor(j*ns.z);
    vec4 y_=floor(j-7.0*x_);
    vec4 x=x_*ns.x+ns.yyyy;
    vec4 y=y_*ns.x+ns.yyyy;
    vec4 h=1.0-abs(x)-abs(y);
    vec4 b0=vec4(x.xy,y.xy);
    vec4 b1=vec4(x.zw,y.zw);
    vec4 s0=floor(b0)*2.0+1.0;
    vec4 s1=floor(b1)*2.0+1.0;
    vec4 sh=-step(h,vec4(0.0));
    vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;
    vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
    vec3 p0=vec3(a0.xy,h.x);
    vec3 p1=vec3(a0.zw,h.y);
    vec3 p2=vec3(a1.xy,h.z);
    vec3 p3=vec3(a1.zw,h.w);
    vec4 norm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
    p0*=norm.x; p1*=norm.y; p2*=norm.z; p3*=norm.w;
    vec4 m=max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0);
    m=m*m;
    return 42.0*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
  }

  // Curl d'un champ potentiel (recette Bridson) : champ de vitesse à
  // divergence nulle, dont les lignes de courant forment naturellement des
  // tourbillons/vortex plutôt qu'un gondolage générique sans structure.
  vec3 curlNoise(vec3 p) {
    const float e = 0.08;
    vec3 dx = vec3(e, 0.0, 0.0);
    vec3 dy = vec3(0.0, e, 0.0);
    vec3 dz = vec3(0.0, 0.0, e);
    vec3 off1 = vec3(0.0, 0.0, 0.0);
    vec3 off2 = vec3(31.7, 9.2, 4.6);
    vec3 off3 = vec3(2.3, 17.1, 71.4);

    float x1 = snoise(p + dy + off3), x2 = snoise(p - dy + off3);
    float y1 = snoise(p + dz + off1), y2 = snoise(p - dz + off1);
    float z1 = snoise(p + dx + off2), z2 = snoise(p - dx + off2);

    float x1b = snoise(p + dz + off2), x2b = snoise(p - dz + off2);
    float y1b = snoise(p + dx + off3), y2b = snoise(p - dx + off3);
    float z1b = snoise(p + dy + off1), z2b = snoise(p - dy + off1);

    float cx = (x1 - x2) - (x1b - x2b);
    float cy = (y1 - y2) - (y1b - y2b);
    float cz = (z1 - z2) - (z1b - z2b);
    return vec3(cx, cy, cz) / (2.0 * e);
  }

  // Somme de curl noise à plusieurs échelles (Complexity octaves,
  // atténuation/fréquence via Octave Multiplier/Scale) : des vortex de
  // tailles différentes se superposent.
  vec3 curlFbm(vec3 p) {
    vec3 sum = vec3(0.0);
    float amp = 1.0;
    float totalAmp = 0.0;
    vec3 pp = p;
    for (int i = 0; i < 6; i++) {
      if (i >= uComplexity) break;
      sum += amp * curlNoise(pp);
      totalAmp += amp;
      pp *= uOctScale;
      amp *= uOctMult;
    }
    return sum / max(totalAmp, 0.0001);
  }

  void main() {
    vec3 dir = normalize(position);

    // Territoires colorés des 11 alphabets : couleur precalculee cote JS
    // (attribute aColor, cf. computeTerritoryColors()) plutot que recalculee
    // ici a chaque frame pour chacune des ~200k particules (75/2.5 density) --
    // le resultat ne depend que de "dir" (fixe par sommet) et des poids/
    // reglages sigma/weightScale, qui ne changent QUE lors d'un reglage
    // admin ou d'une nouvelle punchline, jamais a chaque frame (demande
    // Rudy, 22 septembre 2026, saccades constatees a densite 75). Meme
    // formule exacte, juste deplacee (cf. git blame pour l'ancienne version
    // GLSL si besoin de la retrouver).
    vColor = aColor;

    // "Flow Evolution" : le champ de bruit évolue dans le temps (glisse le
    // long d'un axe), donnant l'impression que la déformation circule sur
    // la sphère plutôt que de juste pulser. Décalage calculé côté JS (cf.
    // update/setPose) : en ligne droite sur la page, en cercle pour l'export
    // vidéo en boucle (sillageLoop.js).
    vec3 noiseP = dir * uFScale + uNoiseOffset;

    // Déplacement 3D complet (pas juste radial) par le champ tourbillonnant
    // -- crée le plissé/vrillé et les vortex façon membrane.
    vec3 curl = curlFbm(noiseP);
    vN = length(curl);

    vec3 displacement = curl * uDisplace;
    vec3 displaced = dir * uRadius + displacement;

    // Confinement (équivalent "Spherical Field" : Radius + Feather) --
    // ramène doucement tout ce qui dépasse un rayon maximal.
    float maxR = uRadius + uContain;
    float rr = length(displaced);
    if (rr > maxR) {
      float excess = rr - maxR;
      float newR = maxR + excess * uFeather;
      displaced *= newR / rr;
    }

    vec4 mvPosition = modelViewMatrix * vec4(displaced, 1.0);

    // Densité en périphérie : en incidence rasante, les particules
    // grossissent/s'intensifient (bord plus dense que le centre).
    vec3 worldPos = (modelMatrix * vec4(displaced, 1.0)).xyz;
    vec3 viewDirW = normalize(cameraPosition - worldPos);
    float facing = abs(dot(normalize(displaced), viewDirW));
    float rim = pow(1.0 - clamp(facing, 0.0, 1.0), 2.0) * uRimBoost;

    gl_PointSize = uSize * (400.0 / -mvPosition.z) * (1.0 + rim * 2.0);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const FRAGMENT_SHADER = `
  uniform float uOpacity;
  varying float vN;
  varying vec3 vColor;

  // Courbe en S (contraste) : creuse les tons sombres et remonte les tons
  // clairs, pour un relief plus marqué qu'un dégradé plat.
  float sCurve(float x) {
    return x * x * (3.0 - 2.0 * x * 0.6 - x * 0.4);
  }

  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv) * 2.0;
    float alpha = exp(-d * d * 3.0) * uOpacity;
    float lum = sCurve(clamp(0.55 + 0.5 * vN, 0.0, 1.0));
    vec3 col = vColor * (0.5 + 1.1 * lum);
    gl_FragColor = vec4(col, alpha);
  }
`;

/**
 * Crée la sphère des alphabets.
 * @param {object} options
 * @param {number} options.radius - rayon de base de la coquille (avant relief).
 * @param {object} [options.settings] - réglages "look" (cf. DEFAULT_SPHERE_SETTINGS).
 * @param {number[]} [options.weights] - poids initiaux (10 valeurs, ordre SCRIPT_ORDER).
 */
export function createAlphabetSphere({ radius, settings, weights } = {}) {
  const group = new THREE.Group();
  let currentSettings = { ...DEFAULT_SPHERE_SETTINGS, ...(settings || {}) };
  let pointsMesh = null;
  let builtCols = -1;
  let builtRows = -1;
  // Multiplicateur d'opacité indépendant de currentSettings.opacity, utilisé
  // pour le fondu d'apparition piloté depuis sillageMain.js (setFade) --
  // évite le flash au chargement (sphère visible avant que le globe ne soit
  // prêt).
  let fadeMultiplier = 1;
  // Partagé par tous les matériaux successifs (buildGeometry) : l'export
  // vidéo le pilote directement, la page le fait avancer dans update().
  const noiseOffset = new THREE.Vector3();
  // gl_PointSize est en pixels réels du canvas : l'export vidéo, rendu à
  // une autre résolution que l'écran, corrige la taille des particules pour
  // garder le même rendu (cf. setPointSizeScale).
  let pointSizeScale = 1;
  // Finesse (photo 30x30 cm, cf. sillageMain.js) : grille detail x plus
  // serrée dans chaque sens, particules detail x plus petites -- même forme
  // et même luminosité d'ensemble (blending additif : detail² fois plus de
  // points, chacun detail² fois plus petit), grain plus fin à l'impression.
  let detail = 1;
  const sizeUniform = () => (currentSettings.pointSize * currentSettings.tightness * pointSizeScale) / detail;

  const weightsArray = new Array(SCRIPT_ORDER.length).fill(0);
  if (weights) setWeights(weights);

  function buildGeometry(cols, rows) {
    if (pointsMesh) {
      group.remove(pointsMesh);
      pointsMesh.geometry.dispose();
      pointsMesh.material.dispose();
    }

    const basePositions = buildCubeSphereGrid(cols, rows);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(basePositions, 3));
    const colors = computeTerritoryColors(basePositions, weightsArray, currentSettings.sigmaBase, currentSettings.weightScale);
    geo.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));

    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uNoiseOffset: { value: noiseOffset },
        uRadius: { value: radius },
        uFScale: { value: currentSettings.fScale },
        uComplexity: { value: currentSettings.complexity },
        uOctMult: { value: currentSettings.octMult },
        uOctScale: { value: currentSettings.octScale },
        uDisplace: { value: currentSettings.displace },
        uContain: { value: currentSettings.contain },
        uFeather: { value: currentSettings.feather },
        uRimBoost: { value: currentSettings.rimBoost },
        uSize: { value: sizeUniform() },
        uOpacity: { value: currentSettings.opacity * fadeMultiplier },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
    });

    pointsMesh = new THREE.Points(geo, mat);
    pointsMesh.frustumCulled = false;
    group.add(pointsMesh);
    builtCols = cols; builtRows = rows;
  }

  function gridSize() {
    const density = currentSettings.density;
    const cols = Math.max(4, Math.round(currentSettings.particlesX * density * detail));
    const rows = Math.max(4, Math.round(currentSettings.particlesY * density * detail));
    return { cols, rows };
  }

  // Recalcule l'attribut aColor (cf. computeTerritoryColors) sur la
  // geometrie deja construite -- appelee quand les poids ou sigmaBase/
  // weightScale changent, jamais par frame.
  function updateColors() {
    if (!pointsMesh) return;
    const positions = pointsMesh.geometry.attributes.position.array;
    const colors = computeTerritoryColors(positions, weightsArray, currentSettings.sigmaBase, currentSettings.weightScale);
    const attr = pointsMesh.geometry.attributes.aColor;
    attr.array.set(colors);
    attr.needsUpdate = true;
  }

  function setWeights(next) {
    for (let i = 0; i < weightsArray.length; i++) {
      weightsArray[i] = Number(next[i]) || 0;
    }
    updateColors();
  }

  function applySettings(next) {
    const prevSigmaBase = currentSettings.sigmaBase;
    const prevWeightScale = currentSettings.weightScale;
    currentSettings = { ...currentSettings, ...next };
    group.scale.setScalar(currentSettings.scale);
    group.rotation.x = THREE.MathUtils.degToRad(currentSettings.tilt);

    const { cols, rows } = gridSize();
    if (cols !== builtCols || rows !== builtRows) {
      buildGeometry(cols, rows);
      return;
    }
    if (!pointsMesh) return;
    const u = pointsMesh.material.uniforms;
    u.uFScale.value = currentSettings.fScale;
    u.uComplexity.value = currentSettings.complexity;
    u.uOctMult.value = currentSettings.octMult;
    u.uOctScale.value = currentSettings.octScale;
    u.uDisplace.value = currentSettings.displace;
    u.uContain.value = currentSettings.contain;
    u.uFeather.value = currentSettings.feather;
    u.uRimBoost.value = currentSettings.rimBoost;
    u.uSize.value = sizeUniform();
    u.uOpacity.value = currentSettings.opacity * fadeMultiplier;
    // sigmaBase/weightScale n'existent plus comme uniforms (cf. aColor,
    // computeTerritoryColors) -- recalcule l'attribut couleur seulement si
    // l'un des deux a vraiment change (pas a chaque reglage admin).
    if (currentSettings.sigmaBase !== prevSigmaBase || currentSettings.weightScale !== prevWeightScale) {
      updateColors();
    }
  }

  function setFade(factor) {
    fadeMultiplier = Math.max(0, Math.min(1, factor));
    if (pointsMesh) pointsMesh.material.uniforms.uOpacity.value = currentSettings.opacity * fadeMultiplier;
  }

  function update(dt, t) {
    group.rotation.y += currentSettings.rotSpeed * dt;
    noiseOffset.set(0, 0, t * currentSettings.flow * 3);
  }

  // Pose exacte imposée image par image par l'export vidéo (sillageLoop.js),
  // à la place de update().
  function getPose() {
    return { rotationY: group.rotation.y, noiseOffset: noiseOffset.toArray() };
  }

  function setPose({ rotationY, noiseOffset: offset }) {
    group.rotation.y = rotationY;
    noiseOffset.fromArray(offset);
  }

  function setPointSizeScale(scale) {
    pointSizeScale = scale;
    if (pointsMesh) pointsMesh.material.uniforms.uSize.value = sizeUniform();
  }

  function setDetail(factor) {
    detail = factor;
    const { cols, rows } = gridSize();
    if (cols !== builtCols || rows !== builtRows) buildGeometry(cols, rows);
    else if (pointsMesh) pointsMesh.material.uniforms.uSize.value = sizeUniform();
  }

  function dispose() {
    if (pointsMesh) {
      pointsMesh.geometry.dispose();
      pointsMesh.material.dispose();
    }
  }

  const { cols, rows } = gridSize();
  buildGeometry(cols, rows);
  group.scale.setScalar(currentSettings.scale);
  group.rotation.x = THREE.MathUtils.degToRad(currentSettings.tilt);

  return {
    object3D: group, setWeights, applySettings, setFade, update, getPose, setPose, setPointSizeScale, setDetail, dispose,
    get settings() { return currentSettings; },
  };
}

/**
 * Compte les punchlines par écriture (champ `script`) à partir de la liste
 * brute renvoyée par /api/generations, et renvoie un tableau de 10 poids
 * dans l'ordre SCRIPT_ORDER -- prêt pour setWeights(). Aucune limite : le
 * compte est utilisé tel quel (c'est la formule de saturation du shader,
 * pas un plafond côté JS, qui empêche toute cassure visuelle).
 */
export function computeWeightsFromGenerations(generations) {
  const counts = new Map();
  for (const gen of generations || []) {
    const script = gen?.script || 'Latn';
    counts.set(script, (counts.get(script) || 0) + 1);
  }
  return SCRIPT_ORDER.map((key) => counts.get(key) || 0);
}
